import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Stack,
  Typography,
  Avatar,
  IconButton,
  Paper,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material'
import { 
  ArrowBack, 
  Edit, 
  Favorite, 
  FavoriteBorder, 
  Star, 
  StarBorder,
  Male,
  Female,
  Cake,
  Pets,
  ColorLens,
} from '@mui/icons-material'
import { useEntryImageUrl } from '../../hooks/useFirebaseQueries'
import EntryForm from '../forms/EntryForm'

const EntryHeader = ({ 
  entry, 
  editMode, 
  setEditMode, 
  form, 
  handleSaveChanges, 
  handleToggleFavorite, 
  handleToggleFoundation,
  updateEntryMutation 
}) => {
  const navigate = useNavigate()
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const { data: imageUrl } = useEntryImageUrl(entry)

  const handleBackClick = () => {
    navigate('/flock-manager')
  }

  const handleCancelEdit = () => {
    setEditMode(false)
    form.reset()
  }

  const getBirdIcon = (sex) => {
    return sex === 'male' ? <Male /> : <Female />
  }

  const getPhaseColor = (phase) => {
    switch (phase) {
      case 'chick': return '#FF9800'
      case 'juvenile': return '#2196F3'
      case 'adult': return '#4CAF50'
      default: return '#757575'
    }
  }

  if (editMode) {
    return (
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handleBackClick} size="large">
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" component="h1">
              Edit {entry?.name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancelEdit}
              disabled={updateEntryMutation?.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveChanges}
              disabled={updateEntryMutation?.isPending}
              startIcon={updateEntryMutation?.isPending ? <CircularProgress size={16} /> : null}
            >
              {updateEntryMutation?.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Box>
        
        <EntryForm form={form} />
      </Paper>
    )
  }

  return (
    <>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        {/* Mobile Layout */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          {/* Header row with back button and edit button */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <IconButton onClick={handleBackClick} size="large">
              <ArrowBack />
            </IconButton>
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => setEditMode(true)}
              size="small"
            >
              Edit
            </Button>
          </Box>
          
          {/* Main content */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <Avatar 
              sx={{ 
                width: 60, 
                height: 60,
                bgcolor: getPhaseColor(entry?.phase),
                fontSize: '1.5rem',
                flexShrink: 0
              }}
              src={imageUrl}
            >
              {!imageUrl && getBirdIcon(entry?.sex)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                <Typography 
                  variant="h4" 
                  component="h1" 
                  sx={{ 
                    fontWeight: 'bold',
                    wordBreak: 'break-word',
                    flex: '1 1 auto',
                    minWidth: 0
                  }}
                >
                  {entry?.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  <IconButton 
                    onClick={handleToggleFavorite} 
                    color={entry?.isFavorited ? "error" : "default"}
                    size="small"
                  >
                    {entry?.isFavorited ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
                  </IconButton>
                  <IconButton 
                    onClick={handleToggleFoundation} 
                    color={entry?.isFoundation ? "warning" : "default"}
                    size="small"
                  >
                    {entry?.isFoundation ? <Star fontSize="small" /> : <StarBorder fontSize="small" />}
                  </IconButton>
                </Box>
              </Box>
              
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Pets fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {entry?.species} • {entry?.variety} {entry?.breed} •
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {entry?.sex === 'male' ? <Male fontSize="small" color="action" /> : <Female fontSize="small" color="action" />}
                    <Typography variant="body2" color="text.secondary">
                      {entry?.sex}
                    </Typography>
                  </Box>
                  {entry?.phase && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Cake fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {entry.phase}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Stack>
            </Box>
          </Box>
        </Box>

        {/* Desktop Layout */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handleBackClick} size="large">
              <ArrowBack />
            </IconButton>
            <Avatar 
              sx={{ 
                width: 80, 
                height: 80,
                bgcolor: getPhaseColor(entry?.phase),
                fontSize: '2rem'
              }}
              src={imageUrl}
            >
              {!imageUrl && getBirdIcon(entry?.sex)}
            </Avatar>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold' }}>
                  {entry?.name}
                </Typography>
                <IconButton 
                  onClick={handleToggleFavorite} 
                  color={entry?.isFavorited ? "error" : "default"}
                  size="large"
                >
                  {entry?.isFavorited ? <Favorite /> : <FavoriteBorder />}
                </IconButton>
                <IconButton 
                  onClick={handleToggleFoundation} 
                  color={entry?.isFoundation ? "warning" : "default"}
                  size="large"
                >
                  {entry?.isFoundation ? <Star /> : <StarBorder />}
                </IconButton>
              </Box>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>                  
                  <Typography variant="body1" color="text.secondary">
                    {entry?.variety} {entry?.breed}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {entry?.sex === 'male' ? <Male fontSize="small" color="action" /> : <Female fontSize="small" color="action" />}
                  <Typography variant="body1" color="text.secondary">
                    {entry?.sex}
                  </Typography>
                </Box>
                {entry?.phase && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Cake fontSize="small" color="action" />
                    <Typography variant="body1" color="text.secondary">
                      {entry.phase}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </Box>
          
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => setEditMode(true)}
            size="large"
          >
            Edit Entry
          </Button>
        </Box>

        {entry?.characteristics && entry.characteristics.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ColorLens fontSize="small" color="action" />
              <Typography variant="subtitle2" color="text.secondary">
                Characteristics
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {entry.characteristics.map((char, index) => (
                <Box
                  key={index}
                  sx={{
                    px: 2,
                    py: 0.5,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    borderRadius: 1,
                    fontSize: '0.875rem',
                    fontWeight: 'medium'
                  }}
                >
                  {char}
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {entry?.name}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default EntryHeader